import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize, forkJoin, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { IChangeSet } from 'src/app/changes-dialog/changes-dialog.component';
import { ColumnFilterComponent } from 'src/app/column-filter/column-filter.component';
import {
  ColumnFilterService,
  ITableColumnFilter,
} from 'src/app/column-filter/column-filter.service';
import { CanEditExperimentPipe } from 'src/app/pipes/can-edit-experiment.pipe';
import { ICExperimentFormatPipe } from 'src/app/pipes/icexperiment-format.pipe';
import { AppStateService } from 'src/app/services/app-state.service';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  AuditedChangeResponse,
  DetailsGeneratedStatus,
  Experiment,
  ExperimentExtended,
  ExperimentType,
  ICExperiment,
  ICExperimentStatus,
  NotesCountMap,
  StudyType,
  User,
} from 'src/app/services/labpartner.service.model';
import {
  EVENT_ARCHIVE_EXPERIMENT,
  EVENT_CHANGE_OWNER,
  EVENT_UNARCHIVE_EXPERIMENT,
  PAGE_NAME_EXPERIMENTSLIST,
} from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { NotesService } from 'src/app/services/notes-service.service';
import { UserAccountService } from 'src/app/services/user-account.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { ExperimentService } from 'src/app/shared/experiment.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';
import {
  ICExperimentDialogComponent,
  ICExperimentDialogData,
  ICExperimentDialogResult,
} from '../experiment-dialog/ic-experiment-dialog.component';

@Component({
  selector: 'ic-experiments',
  templateUrl: './ic-experiments.component.html',
  styleUrls: ['./ic-experiments.component.scss'],
  standalone: false,
})
export class ICExperimentsComponent extends BaseComponent implements OnInit, OnDestroy {
  ICExperimentStatus = ICExperimentStatus;
  @ViewChildren(ColumnFilterComponent) columnFilterQueryList?: QueryList<ColumnFilterComponent>;

  public experiments: ICExperiment[] = [];
  public objectName = 'Experiment';
  public CanCreate = false;
  public displayedColumns = [
    'name',
    'experimentId',
    'dateCreated',
    'expStatus',
    'createdByName',
    'actions',
  ];

  loading = false;

  listData!: MatTableDataSource<any>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  searchKey: string = '';
  devices!: any;
  status!: any;
  allExperimentNoteCounts: NotesCountMap = {};
  showArchivedExperiments: boolean = false;

  currentExperiment: ICExperiment | null = null;
  currentUser?: User;
  currentRoles: string[] = [];

  filterTableName: keyof ITableColumnFilter = 'ic_experiment';

  StudyType = StudyType;

  experimentMonthFilter?: number = 6;

  fromDate: string = '';
  toDate: string = '';

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private service: ExperimentService,
    private apiService: LabpartnerService,
    public accountService: UserAccountService,
    public appState: AppStateService,
    private notesService: NotesService,
    private dialog: MatDialog,
    private router: Router,
    private columnFilterService: ColumnFilterService,
    private notificationService: NotificationService,
    private loggingService: LoggingService,
    private canEditExperimentPipe: CanEditExperimentPipe,
    private dialogService: DialogService,
    private experimentPipe: ICExperimentFormatPipe,
  ) {
    super();
    this.devices = service.devices.sort(x => x.position);
    this.status = service.status;
  }

  async ngOnInit() {
    // await to make sure userlist is available
    await this.accountService.SetUserList();

    // await until the user has been (possibly created and) fetched, set in our account state
    await this.accountService.WaitUntilWeHaveUser();

    this.appState.ExperimentOpened.pipe(takeUntil(this._destroying$)).subscribe(e => {
      this.currentExperiment = e as any;
    });

    this.accountService.currentRoles$.pipe(takeUntil(this._destroying$)).subscribe(roles => {
      this.currentRoles = roles;
    });

    this.accountService.currentUser$.pipe(takeUntil(this._destroying$)).subscribe(user => {
      this.currentUser = user;
    });

    this.columnFilterService.tableFiltersUpdated$
      .pipe(takeUntil(this._destroying$))
      .subscribe(tableName => {
        if (tableName != 'ic_experiment' && tableName != 'all') {
          return;
        }
        this.refreshTableFilter();
      });

    this.columnFilterService.tableFiltersReset$
      .pipe(takeUntil(this._destroying$))
      .subscribe(tableName => {
        if (tableName != 'ic_experiment' && tableName != 'all') {
          return;
        }
        this.columnFilterService.deleteQueryListFilters(this.columnFilterQueryList!);
      });

    this.loadData();

    this.notesService.allExperimentsNotesCountMap
      .pipe(takeUntil(this._destroying$))
      .subscribe(noteCounts => {
        this.allExperimentNoteCounts = noteCounts;
      });

    this.notesService.experimentNotesCountMap
      .pipe(takeUntil(this._destroying$))
      .subscribe(noteCounts => {
        if (noteCounts && noteCounts.Experiment) {
          for (const experimentId of Object.keys(noteCounts.Experiment)) {
            this.allExperimentNoteCounts[+experimentId] =
              noteCounts.Experiment[+experimentId] ??
              this.allExperimentNoteCounts[+experimentId] ??
              0;
          }
        }
      });

    this.loggingService.logPageView(PAGE_NAME_EXPERIMENTSLIST);
  }

  loadData() {
    // Keep empty toDate to get the latest value of the experiments
    this.toDate = '';

    if (this.experimentMonthFilter) {
      const date = new Date();
      date.setMonth(date.getMonth() - this.experimentMonthFilter);
      this.fromDate = date.toISOString();
    } else {
      this.fromDate = '';
    }

    this.loadExperiments();
    this.loadNoteCounts();
  }
  /**
   * Gets the note counts for all the experiments
   */
  loadNoteCounts() {
    this.subscription.add(
      this.apiService
        .getNoteCountsForAllExperiments(this.fromDate, this.toDate)
        .subscribe(noteCounts => {
          const currentNoteCounts = this.notesService.allExperimentsNotesCountMap.getValue();
          this.notesService.allExperimentsNotesCountMap.next({
            ...currentNoteCounts,
            ...noteCounts,
          });
        }),
    );
  }

  loadExperiments() {
    this.loading = true;
    this.subscription.add(
      forkJoin([this.apiService.getICExperimentsList(this.fromDate, this.toDate)])
        .pipe(
          takeUntil(this._destroying$),
          finalize(() => (this.loading = false)),
        )
        .subscribe(results => {
          this.experiments = results[0];

          // Set the experiment properties
          for (const experiment of this.experiments as ExperimentExtended[]) {
            experiment.createdByName = this.accountService.getUserName(experiment.createdBy);
            experiment.deviceName = this.service.getDeviceName(experiment.deviceType);
            experiment.expStatus = this.service.icgetStatus(experiment);
          }

          this.listData = new MatTableDataSource(this.experiments);
          this.setExperiments();
          this.listData.sort = this.sort;
          this.listData.paginator = this.paginator;
          this.listData.filterPredicate = (data: any, filter: string) => {
            const tableFilters = this.columnFilterService.getActiveTableFilters('ic_experiment');
            const filteredColumns = this.columnFilterService.getTableFilterColumns('ic_experiment');

            const columnFilterFn = () => {
              return filteredColumns
                .map(fc => {
                  return tableFilters[fc].has(data[fc]);
                })
                .every(matchesFilters => matchesFilters);
            };

            const displayedColumnsFilterFn = () => {
              return this.displayedColumns.some(ele => {
                if (ele === 'actions' || data[ele] == undefined) {
                  return false;
                }
                let value = data[ele];
                if (ele === 'experimentId') {
                  value = this.experimentPipe.transform(data.experimentId);
                }
                return value?.toString().toLowerCase().indexOf(filter) != -1;
              });
            };

            if (filteredColumns.length && this.searchKey.length) {
              return columnFilterFn() && displayedColumnsFilterFn();
            } else if (filteredColumns.length && !this.searchKey.length) {
              return columnFilterFn();
            } else if (!filteredColumns.length && this.searchKey.length) {
              return displayedColumnsFilterFn();
            } else {
              return true;
            }
          };

          this.refreshTableFilter();
        }),
    );
  }

  protected ngOnDestroyInternal(): void {
    // required by base component. clean up any component specific resources
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  tableDataChanged() {
    this.columnFilterService.refreshQueryListDisplayFilters(
      this.columnFilterQueryList!,
      this.listData,
      this.searchKey,
    );
  }

  onSearchClear() {
    setTimeout(() => {
      this.searchKey = '';
      this.onSearchChange();
    });
  }

  // ChangeOwner functionality is currently disabled for all users.
  // There is no IC admin role defined yet, and IC Writers should not be able to change experiment owners.
  // This method will remain functional but the UI button is disabled until an IC admin role is implemented.
  // TODO: Re-enable the UI button when IC admin role is available and update authorization check accordingly.
  onChangeOwner(row: Experiment, userId: number) {
    console.log('Changing experiment: ' + row.experimentId + ' to userId: ' + userId);
    const fromUserId = row.createdBy;
    this.apiService
      .changeICExperimentOwner(userId, row.experimentId)
      .subscribe({
        next: (result: ICExperiment) => {
          Object.assign(row, { createdByName: this.accountService.getUserName(userId) });

          const props: { [key: string]: number } = {
            ExperimentId: result.experimentId,
            FromUserId: fromUserId,
            ToUserId: userId,
          };
          this.loggingService.logEvent(EVENT_CHANGE_OWNER, props);

          this.notificationService.success(':: Experiment owner updated successfully');
        },
        error: (err: any) => {
          const expectedErr = err.error as AuditedChangeResponse;
          if (expectedErr?.expectedException) {
            this.notificationService.error(expectedErr.message);
          } else {
            this.notificationService.error('Error changing experiment owner. Please try again later.');
          }
        },
      });
  }

  onSearchChange() {
    this.refreshTableFilter();
  }

  onCreate() {
    const dialogConfig = new MatDialogConfig<ICExperimentDialogData>();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.data = {
      isReadOnly: false,
      isEditMode: false,
      experiment: {
        experimentId: 0,
        name: '',
        description: '',
        dateCreated: new Date(),
        dateUpdated: new Date(),
        status: 0,
        type: 0,
        createdBy: 0,
        isArchived: 0,
        isCancelled: false, // Default to false when creating a new experiment
      },
    };

    const dialogRef = this.dialog.open<
      ICExperimentDialogComponent,
      ICExperimentDialogData,
      ICExperimentDialogResult
    >(ICExperimentDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult && dialogResult.success) {
        const data = dialogResult.data as ExperimentExtended;
        data.createdByName = this.accountService.getUserName(data.createdBy);
        data.deviceName = this.service.getDeviceName(data.deviceType);
        data.expStatus = this.service.icgetStatus(data);
        this.listData.data.push(data);
        this.listData._updateChangeSubscription();
      }
    });
  }

  onArchive(row: ExperimentExtended) {
    const changeSet: IChangeSet[] = [];
    changeSet.push({
      identifier: `Experiment ${row.experimentId} ${row.name}`,
      field: 'Experiment',
      oldValue: `UNARCHIVED`,
      newValue: 'ARCHIVED',
    });

    const dialogRef = this.dialogService.openConfirmChangesDialog(
      changeSet,
      row.type === null ? ExperimentType.ResearchAndDevelopment : row.type,
      { isDelete: false },
    );

    dialogRef.afterClosed().subscribe(async data => {
      if (
        data?.submitClicked &&
        (data?.reasonProvided ||
          row.type == ExperimentType.ResearchAndDevelopment ||
          row.type === null)
      ) {
        this.apiService.archiveICExperiment(row.experimentId, data.batchReason).subscribe(
          () => {
            this.loadData();

            this.notificationService.success('Experiment archived');

            const props: { [key: string]: string | number } = {
              ExperimentId: row.experimentId,
              DeviceType: row.deviceType,
              ExperimentOwner: this.currentExperiment?.createdBy ?? 0,
            };
            this.loggingService.logEvent(EVENT_ARCHIVE_EXPERIMENT, props);
          },
          (err: any) => {
            const expectedErr = err.error as AuditedChangeResponse;
            if (expectedErr.expectedException) {
              this.notificationService.error(expectedErr.message);
            } else {
              throw err;
            }
          },
        );
      }
    });
  }

  onUnarchive(row: any) {
    const changeSet: IChangeSet[] = [];
    changeSet.push({
      identifier: `Experiment ${row.experimentId} ${row.name}`,
      field: 'Experiment',
      oldValue: `ARCHIVED`,
      newValue: 'UNARCHIVED',
    });

    const dialogRef = this.dialogService.openConfirmChangesDialog(
      changeSet,
      row.type === null ? ExperimentType.ResearchAndDevelopment : row.type,
      { isDelete: false },
    );

    dialogRef.afterClosed().subscribe(async data => {
      if (
        data?.submitClicked &&
        (data?.reasonProvided ||
          row.type == ExperimentType.ResearchAndDevelopment ||
          row.type === null)
      ) {
        this.apiService.unarchiveICExperiment(row.experimentId, data.batchReason).subscribe(
          () => {
            this.loadData();

            this.notificationService.success('Experiment unarchived');

            const props: { [key: string]: string | number } = {
              ExperimentId: row.experimentId,
              DeviceType: row.deviceType,
              ExperimentOwner: this.currentExperiment?.createdBy ?? 0,
            };
            this.loggingService.logEvent(EVENT_UNARCHIVE_EXPERIMENT, props);
          },
          (err: any) => {
            const expectedErr = err.error as AuditedChangeResponse;
            if (expectedErr.expectedException) {
              this.notificationService.error(expectedErr.message);
            } else {
              throw err;
            }
          },
        );
      }
    });
  }

  onEdit(row: any) {
    const canEdit = this.canEditExperimentPipe.transform(this.currentUser, this.currentRoles, row);
    const currentExperiment = { ...row };

    const dialogConfig: MatDialogConfig<ICExperimentDialogData> = {
      disableClose: false,
      autoFocus: true,
      width: '60%',
      data: {
        isReadOnly: !canEdit,
        isEditMode: true,
        experiment: currentExperiment,
      },
    };

    const dialogRef = this.dialog.open<
      ICExperimentDialogComponent,
      ICExperimentDialogData,
      ICExperimentDialogResult
    >(ICExperimentDialogComponent, dialogConfig);

    this.subscription.add(
      dialogRef.afterClosed().subscribe((dialogResult: ICExperimentDialogResult | undefined) => {
        if (dialogResult?.data) {
          const existingRowIdx = this.listData.data.findIndex(
            (exp: Experiment) => exp.experimentId == dialogResult.data.experimentId,
          );

          const existingRowData: ExperimentExtended = { ...row, ...dialogResult.data };

          this.listData.data.splice(existingRowIdx, 1, existingRowData);
          this.listData._updateChangeSubscription();
        }
      }),
    );
  }

  onOpenExperiment(exp: Experiment) {
    console.log('Opening experiment: ' + exp.experimentId);
    console.log('exp.status', exp.status);
    console.log('this.router', this.router);
    // "smart" open. take user to where we think they might want to go, based on status
    if (exp.status < DetailsGeneratedStatus) {
      this.router.navigate(['/image-capture/experiment-detail', exp.experimentId, 0]);
    } else {
      this.router.navigate(['/image-capture/experiment-detail', exp.experimentId, 3]);
    }
  }

  // Toggle the showArchivedExperiments flag
  showArchivedExperimentsToggle() {
    this.showArchivedExperiments = !this.showArchivedExperiments;
    this.setExperiments();
  }

  // Set the experiments to show based on the showArchivedExperiments flag
  setExperiments() {
    this.listData.data = this.showArchivedExperiments
      ? this.experiments
      : this.experiments.filter(e => e.isArchived == 0);
    this.listData._updateChangeSubscription();
  }

  private refreshTableFilter() {
    const filteredColumns = this.columnFilterService.getTableFilterColumns('ic_experiment');
    this.listData.filter = this.searchKey.length
      ? this.searchKey.trim().toLowerCase()
      : filteredColumns.length
        ? 'custom-filter'
        : '';
  }

  onReopen(row: any) {
    this.subscription.add(
      this.dialogService
        .openConfirmDialog(`Are you sure you want to reopen this experiment?`, {
          isOk: false,
          isV2: true,
          title: 'Reopen Experiment',
          width: '400px',
        })
        .afterClosed()
        .pipe(
          switchMap((result: any) => {
            if (result) {
              // If the user confirmed the reopening, proceed with the API call
              return this.apiService.icReopenExperiment(row.experimentId).pipe(
                tap(() => {
                  this.notificationService.success('Experiment reopened successfully.');
                  this.onOpenExperiment(row);
                }),
                catchError(() => {
                  this.notificationService.error(
                    'Error reopening experiment. Please try again later.',
                  );
                  return EMPTY;
                }),
              );
            } else {
              // the user closed the modal or clicked "No", just show a warning message
              this.notificationService.warn('Experiment reopening aborted.');
              return EMPTY; // no API call is made
            }
          }),
        )
        .subscribe(),
    );
  }
}
